/* eslint-disable @typescript-eslint/no-explicit-any */
import { useCallback, useEffect, useRef, useState } from 'react';

import { useAuth } from '@/state/auth';

interface ConversationPollingMessage {
  type:
    | 'START_POLLING'
    | 'STOP_POLLING'
    | 'UPDATE_CONVERSATION'
    | 'SEND_MESSAGE';
  conversationId?: string;
  apiUrl?: string;
  authToken?: string;
  impersonatedUser?: string;
  messageInterval?: number;
  conversationInterval?: number;
  messagePayload?: {
    conversationId: string;
    message: string;
    mode?: 'query' | 'agent';
  };
}

interface ConversationResponse {
  type:
    | 'CONVERSATIONS_UPDATE'
    | 'MESSAGES_UPDATE'
    | 'MESSAGE_SENT'
    | 'ERROR'
    | 'PENDING_MESSAGES_UPDATE';
  data?: any;
  pendingMessages?: Array<{ id: string; text: string; timestamp: number }>;
  error?: string;
  conversationId?: string;
}

interface ChatWorkerState {
  conversations: any[];
  messages: any[];
  pendingMessages: Array<{ id: string; text: string; timestamp: number }>;
  conversationsLoading: boolean;
  messagesLoading: boolean;
  error: string | null;
  sendMessage: (
    conversationId: string,
    message: string,
    mode?: 'query' | 'agent'
  ) => Promise<void>;
  createConversation: (
    message: string,
    mode?: 'query' | 'agent'
  ) => Promise<any>;
}

export function useChatWorker(conversationId?: string): ChatWorkerState {
  const [conversations, setConversations] = useState<any[]>([]);
  const [messages, setMessages] = useState<any[]>([]);
  const [pendingMessages, setPendingMessages] = useState<
    Array<{ id: string; text: string; timestamp: number }>
  >([]);
  const [conversationsLoading, setConversationsLoading] = useState(true);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const workerRef = useRef<Worker | null>(null);
  const workerReadyRef = useRef<boolean>(false);
  const { getToken, api, isImpersonating, friend, me } = useAuth();

  // Track current user context to detect account switches
  const currentUserRef = useRef<string>('');
  const currentUser = isImpersonating ? friend : me;

  const initializeWorker = useCallback(async () => {
    try {
      // Create worker
      if (!workerRef.current) {
        workerRef.current = new Worker(
          new URL('../workers/conversationWorker.ts', import.meta.url),
          { type: 'module' }
        );

        workerRef.current.addEventListener(
          'message',
          (event: MessageEvent<ConversationResponse>) => {
            const { type, data, error: workerError } = event.data;

            switch (type) {
              case 'CONVERSATIONS_UPDATE':
                setConversations(data || []);
                setConversationsLoading(false);
                setError(null);
                break;

              case 'MESSAGES_UPDATE':
                setMessages(data || []);
                setMessagesLoading(false);
                setError(null);
                break;

              case 'PENDING_MESSAGES_UPDATE':
                setPendingMessages(event.data.pendingMessages || []);
                break;

              case 'MESSAGE_SENT':
                setError(null);
                break;

              case 'ERROR':
                console.error('Worker error event:', workerError);
                setError(workerError || 'Unknown error');
                setConversationsLoading(false);
                setMessagesLoading(false);
                break;
            }
          }
        );

        workerRef.current.addEventListener('error', error => {
          console.error('Worker error:', error);
          setError('Worker failed to initialize');
          setConversationsLoading(false);
          setMessagesLoading(false);
        });
      }

      // Get auth token
      const token = await getToken();
      if (!token) {
        setError('No authentication token available');
        return;
      }

      // Start polling
      const message: ConversationPollingMessage = {
        type: 'START_POLLING',
        conversationId,
        apiUrl: api,
        authToken: token,
        impersonatedUser: isImpersonating ? friend : undefined,
        messageInterval: 3000, // 3 seconds for messages
        conversationInterval: 10000, // 10 seconds for conversations (catch status changes quickly)
      };

      workerRef.current.postMessage(message);

      if (conversationId) {
        setMessagesLoading(true);
      }

      // Mark worker as ready after successful initialization
      workerReadyRef.current = true;
    } catch (err) {
      console.error('Failed to initialize worker:', err);
      setError(
        err instanceof Error ? err.message : 'Failed to initialize worker'
      );
      setConversationsLoading(false);
      setMessagesLoading(false);
      workerReadyRef.current = false;
    }
  }, [conversationId, getToken, api, currentUser, isImpersonating, friend]);

  const updateConversation = useCallback(
    async (newConversationId?: string) => {
      if (workerRef.current) {
        const token = await getToken();
        const message: ConversationPollingMessage = {
          type: 'UPDATE_CONVERSATION',
          conversationId: newConversationId,
          apiUrl: api,
          authToken: token,
          impersonatedUser: isImpersonating ? friend : undefined,
          messageInterval: 10000,
          conversationInterval: 30000,
        };
        workerRef.current.postMessage(message);

        if (newConversationId) {
          setMessages([]);
          setMessagesLoading(true);
        } else {
          setMessages([]);
          setMessagesLoading(false);
        }
      }
    },
    [getToken, api, currentUser, isImpersonating, friend]
  );

  const sendMessage = useCallback(
    async (
      conversationId: string,
      message: string,
      mode?: 'query' | 'agent'
    ) => {
      // Wait for worker to be ready with exponential backoff
      let attempts = 0;
      while ((!workerRef.current || !workerReadyRef.current) && attempts < 10) {
        await new Promise(resolve =>
          setTimeout(resolve, 100 * Math.pow(2, attempts))
        );
        attempts++;
      }

      if (!workerRef.current || !workerReadyRef.current) {
        const error = new Error('Worker not initialized after waiting');
        console.error('Send message failed:', error);
        throw error;
      }

      const messagePayload: ConversationPollingMessage = {
        type: 'SEND_MESSAGE',
        messagePayload: {
          conversationId,
          message,
          mode,
        },
      };

      workerRef.current.postMessage(messagePayload);
    },
    [currentUser, isImpersonating, friend]
  );

  const createConversation = useCallback(
    async (message: string, mode?: 'query' | 'agent'): Promise<any> => {
      try {
        const token = await getToken();
        const headers: Record<string, string> = {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        };

        // Add impersonation header if impersonating
        if (isImpersonating && friend) {
          headers['account'] = friend;
        }

        const response = await fetch(`${api}/planner`, {
          method: 'POST',
          headers,
          body: JSON.stringify({
            conversationId: '',
            message: message,
            mode: mode || 'query',
          }),
        });

        if (!response.ok) {
          const errorBody = await response.text();
          throw new Error(
            `HTTP ${response.status}: ${response.statusText} - ${errorBody}`
          );
        }

        const data = await response.json();

        if (!data.conversation) {
          throw new Error('No conversation returned from server');
        }

        return data.conversation;
      } catch (err) {
        console.error('Failed to create conversation:', err);
        throw err;
      }
    },
    [getToken, api, currentUser, isImpersonating, friend]
  );

  // Reset worker when user context changes (account switching)
  useEffect(() => {
    if (currentUser && currentUserRef.current !== currentUser) {
      // User context changed - reset everything
      if (workerRef.current) {
        workerRef.current.terminate();
        workerRef.current = null;
      }
      workerReadyRef.current = false;

      // Clear all state
      setConversations([]);
      setMessages([]);
      setPendingMessages([]); // Clear pending messages when switching accounts
      setConversationsLoading(true);
      setMessagesLoading(false);
      setError(null);

      // Update current user tracking
      currentUserRef.current = currentUser;

      // Reinitialize worker with new user context
      initializeWorker();
    }
  }, [currentUser, initializeWorker]);

  // Initialize worker on mount
  useEffect(() => {
    if (!currentUserRef.current && currentUser) {
      currentUserRef.current = currentUser;
      initializeWorker();
    }
    return () => {
      if (workerRef.current) {
        workerRef.current.postMessage({ type: 'STOP_POLLING' });
      }
    };
  }, [initializeWorker, currentUser]);

  // Update conversation when conversationId changes
  useEffect(() => {
    updateConversation(conversationId);
  }, [conversationId, updateConversation]);

  // Refresh token periodically to prevent expiration issues
  useEffect(() => {
    const refreshToken = async () => {
      if (workerRef.current && workerReadyRef.current) {
        try {
          const token = await getToken();
          if (token) {
            // Send updated token to worker
            const message: ConversationPollingMessage = {
              type: 'UPDATE_CONVERSATION',
              conversationId,
              apiUrl: api,
              authToken: token,
              impersonatedUser: isImpersonating ? friend : undefined,
              messageInterval: 3000,
              conversationInterval: 30000,
            };
            workerRef.current.postMessage(message);
          }
        } catch (error) {
          console.error('Failed to refresh token for worker:', error);
        }
      }
    };

    // Refresh token every 10 seconds to ensure worker always has fresh token
    const interval = setInterval(refreshToken, 10 * 1000);
    return () => clearInterval(interval);
  }, [conversationId, getToken, api, isImpersonating, friend]);

  // Cleanup worker on unmount
  useEffect(() => {
    return () => {
      if (workerRef.current) {
        workerRef.current.terminate();
        workerRef.current = null;
      }
    };
  }, []);

  return {
    conversations,
    messages,
    pendingMessages,
    conversationsLoading,
    messagesLoading,
    error,
    sendMessage,
    createConversation,
  };
}
